/**
 * Executes `callback` with the provided `args` only if every arg is non-null
 * and non-undefined. If any arg is null or undefined the callback is skipped
 * and a resolved Promise containing `fallback` is returned instead.
 *
 * Designed as a React Query `queryFn` guard for cases where required
 * parameters (e.g. user ID, role) may not be available yet. This avoids
 * spurious Firestore reads while the auth state is still loading.
 *
 * @param callback - Async function to invoke when all args are present.
 * @param fallback - Value to resolve with when any arg is absent.
 * @param args     - Arguments forwarded to `callback` after the null-check.
 * @returns Promise resolving to the callback result, or `fallback` when any
 *          arg is null/undefined.
 *
 * @example
 * // Only queries Firestore once userId is available
 * queryFn: () => executeIfArgsNotNull(listInstituteByUser, [], role, userId)
 */
export function executeIfArgsNotNull<T>(callback: (...args:any[]) => Promise<T>, value: T, ...args: any[]): Promise<T> {
    if (args.some(arg => arg === null || arg === undefined)) {
        return Promise.resolve(value)
    }
    return callback(...args)
}